import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils";

/* ENSSA Button — 44px default touch target, kid 88px for Year-1
   screener targets. Destructive variant is for "delete saved view"
   UI ONLY; never for high-risk student status (use <RiskBadge />). */

const buttonVariants = cva(
  "inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md text-sm font-bold " +
    "transition-colors duration-[var(--duration-fast)] ease-[var(--ease-standard)] " +
    "focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/35 " +
    "disabled:pointer-events-none disabled:opacity-40 " +
    "[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:     "bg-primary text-primary-foreground hover:opacity-90 active:opacity-80",
        secondary:   "bg-secondary text-secondary-foreground hover:bg-accent hover:text-accent-foreground",
        outline:     "border border-input bg-card text-foreground hover:bg-accent hover:text-accent-foreground",
        ghost:       "text-foreground hover:bg-accent hover:text-accent-foreground",
        link:        "text-primary underline-offset-4 hover:underline",
        destructive: "bg-destructive text-destructive-foreground hover:opacity-90",
        // ENSSA brand variants — preserve legacy <Btn> forest-green palette
        brand:       "bg-[var(--risk-on-track)] text-white border border-[var(--risk-on-track)] hover:opacity-90 active:opacity-80",
        "brand-outline": "bg-transparent text-[var(--risk-on-track)] border border-[var(--risk-on-track)] hover:bg-[color-mix(in_oklab,var(--risk-on-track)_8%,white)]",
        "brand-neutral": "bg-white text-[var(--fg-1)] border border-[var(--border-default)] hover:bg-[var(--ink-50)]",
      },
      size: {
        sm:        "h-9 px-3 text-sm",
        default:   "h-touch px-4 text-base",
        lg:        "h-12 px-6 text-base",
        kid:       "h-touch-kid px-8 text-2xl rounded-xl",
        icon:      "size-touch",
        "icon-sm": "size-9",
        // legacy <Btn>: py 8px / px 16px, 14px font, 700 weight
        legacy:    "py-2 px-4 text-sm font-bold rounded-md",
      },
    },
    defaultVariants: { variant: "default", size: "default" },
  },
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, ...props }, ref) => (
    <button
      className={cn(buttonVariants({ variant, size, className }))}
      ref={ref}
      {...props}
    />
  ),
);
Button.displayName = "Button";

export { buttonVariants };
